import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Stack,
  CircularProgress,
  Divider,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DownloadIcon from '@mui/icons-material/Download';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import { EntityType } from 'shared/enums';
import { useParams } from 'react-router-dom';
import { AISystemsService } from 'services/AISystemService';
import AuditDocument from '../system-failure/AuditDocument';

interface UpdatedFieldValue {
  old?: any;
  new?: any;
}

interface UpdatedFields {
  [key: string]: UpdatedFieldValue;
}

interface ActionId {
  _id: string;
  name: string;
}

interface HistoryData {
  _id: string;
  action: 'linked' | 'unlinked' | 'downloaded' | 'uploaded' | 'removed';
  actionId?: ActionId;
  changeSummary?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  description: string | null;
  entityType: string;
  entityId: string;
  isActive: boolean;
  newStatus: string | null;
  organizationId: string;
  previousStatus: string | null;
  updatedFields: UpdatedFields;
  userName?: string;
  name?: string;
  version: number;
}

interface AIAuditTrailProps {
  title?: string;
  entityType: EntityType;
  onError?: (error: any) => void;
}

const AIAuditTrail: React.FC<AIAuditTrailProps> = ({
  title,
  entityType,
  onError,
}) => {
  const theme = useTheme();
  const [historyData, setHistoryData] = useState<HistoryData[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { aiSystemId } = useParams<{
    aiSystemId?: string;
  }>();

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getActionIcon = (
    action: string,
    color?: string
  ): React.ReactElement => {
    const iconColor = color || theme.palette.primary.main;
    switch (action) {
      case 'uploaded':
        return <LinkIcon sx={{ color: iconColor }} />;
      case 'removed':
        return <LinkOffIcon sx={{ color: iconColor }} />;
      case 'downloaded':
        return <DownloadIcon sx={{ color: iconColor }} />;

      default:
        return <DescriptionIcon sx={{ color: iconColor }} />;
    }
  };

  const getActionChip = (action: string): React.ReactElement => {
    switch (action) {
      case 'uploaded':
        return <Chip label="Linked" size="small" variant="outlined" />;
      case 'removed':
        return <Chip label="Unlinked" size="small" variant="outlined" />;
      case 'downloaded':
        return <Chip label="Downloaded" size="small" variant="outlined" />;

      default:
        return <Chip label={action} size="small" variant="outlined" />;
    }
  };

  const getLinkedEntityName = (history: HistoryData): string | null => {
    if (history.action === 'linked' && history.updatedFields?.linkedName?.new) {
      return history.updatedFields.linkedName.new;
    }
    if (
      history.action === 'unlinked' &&
      history.updatedFields?.linkedName?.old
    ) {
      return history.updatedFields.linkedName.old;
    }
    return null;
  };

  const getLinkedEntityType = (history: HistoryData): string | null => {
    if (history.action === 'linked' && history.updatedFields?.linkedType?.new) {
      return history.updatedFields.linkedType.new;
    }
    if (
      history.action === 'unlinked' &&
      history.updatedFields?.linkedType?.old
    ) {
      return history.updatedFields.linkedType.old;
    }
    return null;
  };

  const fetchHistory = (): void => {
    if (!aiSystemId) return;

    setLoading(true);

    if (entityType === EntityType.AI_SYSTEM_TEVV) {
      AISystemsService.getAISystemTEVVHistory(aiSystemId)
        .then((response) => {
          const historyArray = response?.data?.data || response?.data || [];
          setHistoryData(historyArray);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching TEVV history:', err);
          setHistoryData(null);
          setLoading(false);
          if (onError) onError(err);
        });
    } else {
      AISystemsService.getAISystemFailureHistory(aiSystemId)
        .then((response) => {
          console.log(response, 'RESPONSE getAISystemFailureHistory');
          const historyArray = response?.data?.data || response?.data || [];
          setHistoryData(historyArray);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching failure history:', err);
          setHistoryData(null);
          setLoading(false);
          if (onError) onError(err);
        });
    }
  };
  useEffect(() => {
    if (entityType && aiSystemId) {
      fetchHistory();
    }
  }, [entityType, aiSystemId]);

  if (loading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  let content;

  if (entityType === EntityType.AI_SYSTEM_FAILURE) {
    content = <AuditDocument />;
  } else {
    content = (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <DescriptionIcon
          sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }}
        />
        <Typography color="text.secondary">
          No history records found.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
      </Box>

      {!historyData || historyData.length === 0 ? (
        content
      ) : (
        <Stack spacing={2}>
          {historyData.map((history, index) => (
            <Card key={history._id || index} variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mb: 2 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                    }}
                  >
                    {getActionIcon(history.action)}
                  </Box>

                  {getActionChip(history.action)}

                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(history.createdAt)}
                    </Typography>
                  </Stack>
                </Stack>
                {['uploaded', 'removed', 'downloaded'].includes(
                  history.action
                ) &&
                  history.changeSummary && (
                    <Box sx={{ mt: history.action === 'downloaded' ? 0 : 2 }}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <Typography variant="body2">
                          {history.changeSummary}
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                {(history.action === 'linked' ||
                  history.action === 'unlinked') && (
                  <Box sx={{ mt: 2 }}>
                    {(() => {
                      const entityName = getLinkedEntityName(history);
                      const entityTypeVal = getLinkedEntityType(history);
                      if (!entityName && !entityTypeVal) return null;
                      return (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: alpha(
                              history.action === 'linked'
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                              0.04
                            ),
                            borderRadius: 2,
                            borderLeft: `3px solid ${
                              history.action === 'linked'
                                ? theme.palette.success.main
                                : theme.palette.error.main
                            }`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 1 }}
                          >
                            {history.action === 'linked'
                              ? '📎 Linked Document Details'
                              : '🔗 Unlinked Document Details'}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="baseline"
                            flexWrap="wrap"
                          >
                            {entityTypeVal && (
                              <Chip
                                label={entityTypeVal}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  fontWeight: 500,
                                }}
                              />
                            )}
                            <Typography variant="body2" fontWeight={500}>
                              {entityName}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                {(history.action === 'downloaded' ||
                  history.action === 'uploaded') &&
                  history.updatedFields &&
                  Object.keys(history.updatedFields).filter(
                    (key) =>
                      key !== '_details' &&
                      key !== 'linkedName' &&
                      key !== 'documentName'
                  ).length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mb: 1.5 }}
                      >
                        Changes Made:
                      </Typography>
                      <Stack spacing={1.5}>
                        {Object.entries(history.updatedFields)
                          .filter(
                            ([key]) =>
                              key !== '_details' && key !== 'documentName'
                          )
                          .map(([field, value]) => (
                            <Box
                              key={field}
                              sx={{
                                p: 1.5,
                                bgcolor: alpha(
                                  theme.palette.background.default,
                                  0.5
                                ),
                                borderRadius: 1.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color="text.secondary"
                              >
                                {field
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, (str) => str.toUpperCase())}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={2}
                                sx={{ mt: 0.5 }}
                                flexWrap="wrap"
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    From:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word' }}
                                  >
                                    {value?.old !== undefined &&
                                    value?.old !== null
                                      ? String(value.old)
                                      : '—'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    To:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      wordBreak: 'break-word',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {value?.new !== undefined &&
                                    value?.new !== null
                                      ? String(value.new)
                                      : '—'}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                          ))}
                      </Stack>
                    </Box>
                  )}

                {/* Status change indicator (if present) */}
                {(history.previousStatus || history.newStatus) &&
                  history.previousStatus !== history.newStatus && (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Status changed from
                      </Typography>
                      <Chip
                        label={history.previousStatus || 'Pending'}
                        size="small"
                        variant="outlined"
                        sx={{ bgcolor: alpha(theme.palette.grey[500], 0.1) }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        to
                      </Typography>
                      <Chip
                        label={history.newStatus || 'Unknown'}
                        size="small"
                        color="primary"
                        variant="filled"
                      />
                    </Box>
                  )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default AIAuditTrail;
